<template>
	<div v-if="selectedStudy && currentTab" class="ManageRespondents">
		<v-row
      class="mb-6"
      no-gutters
    >
      <v-col
        md="4"
      >
				<v-row no-gutters>
					<h2 class="left-align">Manage Respondent Accounts</h2>
				</v-row>
				<v-row no-gutters>
					<v-col>
						<p class="p3 left-align"><label>Filter by username</label></p>
					</v-col>
					<v-col></v-col>
				</v-row>
				<v-row no-gutters>
					<v-col>
						<v-text-field
							ref="firstFocusable"
							outlined
							placeholder="Type any part of username"
							single-line
							hide-details
							v-model="filterUserNameValue"
							:aria-label="'Filter by username: ' + filterUserNameValue"
						></v-text-field>
					</v-col>
					<v-col>
						<v-btn @click="clearFilters()" class="btn-default" text>Clear Filter</v-btn>
					</v-col>
				</v-row>
			</v-col>
			<v-col
        md="8" cols="12"
      >
				<div class="help-manage-respondents">
					<div class="manage-respondent-text">
						<p class="p3 left-align">Manage details of existing respondent accounts by resetting or unlocking passwords for your respondents, and deleting respondent intake and username.</p>
						<p class="p3 left-align">
							Unlock a respondent's account if it is locked due to multiple incorrect login attempts.
						</p>
						<p class="p3 left-align">
							Lock a respondent's account to prevent access to the respondent website or if a respondent has asked to be removed from a study.
						</p>
						<p class="p3 left-align"><strong>Note:</strong> Respondent data cannot be retrieved once deleted.</p>
					</div>
				</div>
			</v-col>
		</v-row>
		<v-btn class="btn-link text-none" text @click="selectAll()">Select All</v-btn>
		<v-btn class="btn-link text-none" text @click="deselectAll()">Deselect All</v-btn>
		<v-divider vertical color="primary" style="display: inline;"></v-divider>
		<v-btn class="btn-link text-none" text @click="deleteDialog = true">Delete Selected</v-btn>
		<div v-show="headers.length !== 0" class="track-respondents-table-container">
			<v-data-table
				v-model="selected"
				:headers="headers"
				:items="respondents"
				item-key="userName"
				show-select
				class="elevation-1"
				:page.sync="page"
				:items-per-page="itemsPerPage"
				hide-default-footer
				@page-count="pageCount = $event"
			>
				<template v-for="h in headers" v-slot:[`header.${h.value}`]="{ header }">
					<v-tooltip :disabled="h.hover === ''" :key="h.value" bottom color="#333">
						<template v-slot:activator="{ on }">
							<span v-on="on" class="columnTitleText" tabIndex="0" v-on:keyup.enter="(e) => e.target.click()">{{h.text}}</span>
						</template>
						<span>{{h.hover}}</span>
					</v-tooltip>
				</template>
				<!-- for the check box header -->
				<template v-slot:header.data-table-select="{ value, selected }">
					<v-checkbox
            :value="value"
            :selected="selected"
						aria-label="select all"
            hide-details
            class="mt-0"
						@change="checkboxHeader"
					>
					</v-checkbox>
				</template>
				<!-- for the check boxes -->
				<template v-slot:item.data-table-select="{ item, isSelected }">
					<v-checkbox
						:value="isSelected"
            :isSelected="item"
						aria-label="respondent"
            hide-details
            class="mt-0"
						@change="checkboxData(item)"
						>
					</v-checkbox>
				</template>
				<template v-slot:item.dateLastLogin="{ item }">
					{{ getFormattedDate(item.dateLastLogin) }}
				</template>
				<template v-slot:item.lockBox="{ item }">
					<v-checkbox
						v-model="item.lockedOut"
						aria-label="account lock"
						@click="changeLockStatus(item)"
					></v-checkbox>
				</template>
				<template v-slot:item.resetBtn="{ item }">
					<v-btn text class="btn-link-left text-none" @click="resetPasswordDialog = true, resetPassRespondent = item">Reset Password</v-btn>
				</template>
			</v-data-table>
			<div class="pagination-container">
				<v-row>
					<v-col :cols="2">
						<v-text-field
							:value="itemsPerPage"
							label="Items per page"
							type="number"
							min="1"
							max="15"
							@input="itemsPerPage = parseInt($event, 10)"
						></v-text-field>
					</v-col>
					<v-col :cols="10">
						<v-pagination
							v-model="page"
							:length="pageCount"
						></v-pagination>
					</v-col>
				</v-row>
			</div>
		</div>
		<foe-dialog
      v-model="resetPasswordDialog"
			:dialogW="resetPasswordDialog"
      persistent
			max-width="600px"
			class="reset-respondent-password-dialog"
    >
      <v-card class="reset-respondent-password-card" v-if="resetPassRespondent">
        <v-card-title>Change {{resetPassRespondent.userName}} Respondent's Password</v-card-title>
        <v-card-text>
					<p class="p3 left-align">The password must be:</p>
					<ul>
						<li class="p3 left-align">8-14 characters</li>
						<li class="p3 left-align">have at least one special character (no single or double quotes)</li>
						<li class="p3 left-align">have a combination of letters and numbers and/or a combination of upper and lower case letters</li>
					</ul>
					<br>
					<v-form
						ref="form"
						v-model="validPassword"
						lazy-validation
					>
						<v-text-field
							v-model="newPassword"
							hide-details="auto"
							outlined
							label="*New Password"
							type="password"
							:rules="passwordRules"
						></v-text-field>
						<br>
						<v-text-field
							v-model="confirmPassword"
							hide-details="auto"
							outlined
							:error="errorConfirmPassword"
							:error-messages="errorConfirmPassword ? 'Passwords must match':[]"
							label="*Confirm Password"
							type="password"
						></v-text-field>
					</v-form>
				</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
					<v-btn
            text
						class="btn-default"
            @click="resetPasswordDialog = false, resetPasswordForm()"
          >
            Cancel
          </v-btn>
          <v-btn
            text
						class="btn-default"
						:loading="isPasswordResetLoading"
            @click="resetPassword()"
          >
            Submit
          </v-btn>
        </v-card-actions>
      </v-card>
    </foe-dialog>

		<foe-dialog
      v-model="deleteDialog"
			:dialogW="deleteDialog"
      persistent
			max-width="600px"
			class="reset-respondent-password-dialog"
    >
      <v-card class="reset-respondent-password-card">
        <v-card-title>Confirm Delete</v-card-title>
        <v-card-text>
				Are you sure you want to delete these respondents: 	{{ selectedText }}
				</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
					<v-btn
            text
						class="btn-default"
            @click="deleteDialog = false"
          >
            No
          </v-btn>
          <v-btn
            text
						class="btn-default"
            @click="deleteSelected(); deleteDialog = false"
          >
            Yes
          </v-btn>
        </v-card-actions>
      </v-card>
    </foe-dialog>
	</div>
</template>

<script>
import { researcherApiService } from '@/services/researcherApiService.js';
import { authService } from '@/services/authService.js';
import { utilityService } from '@/services/utilityService.js';
export default {
	name: 'ManageRespondents',
	props: {
	},
	data: () => ({
		selected: [],
		respondents: [],
		studyUuid: '',
		page: 1,
		pageCount: 0,
		itemsPerPage: 10,
		filterUserNameValue: '',
		resetPassRespondent: null,
		resetPasswordDialog: false,
		deleteDialog: false,
		validPassword: true,
		newPassword: '',
		confirmPassword: '',
		errorConfirmPassword: false,
		isPasswordResetLoading: false,
		passwordRules: [
			v => v !== '' || 'This section is required',
			v => (v.length > 7 && v.length < 15) || 'The password does not meet the length requirement.'
		]
	}),
	mounted () {
	},
	computed: {
		headers: function () {
			return [
				{ text: 'Username', value: 'userName', hover: '', class: 'data-table-header', filter: this.filterUsername },
				{ text: 'Last Login Date', value: 'dateLastLogin', hover: '', class: 'data-table-header' },
				{ text: 'Account Locked?', value: 'lockBox', hover: '', class: 'data-table-header' },
				{ text: 'Reset Password', value: 'resetBtn', hover: '', class: 'data-table-header' }
			]
		},
		selectedStudy: function () {
			// If the selectedStudy changes, the radio buttons and checkbox properties need to be updated.
			if (this.studyUuid !== this.$store.getters.getSelectedStudy.studyUuid) {
				this.updateSelected();
			}
			return this.$store.getters.getSelectedStudy;
		},
		currentTab: function () {
			const tabPosition = {
				tabIndex: parseInt(this.$store.getters.getCurrentStudyTabIndex),
				subTabIndex: parseInt(this.$store.getters.getCurrentStudySubTabIndex)
			};

			this.checkTabMatch(tabPosition);

			return tabPosition;
		},
		selectedText: function () {
			var allNames = [];
			for (const piece of this.selected) {
				allNames.push(piece.userName)
			}
			return allNames.toString()
		}
	},
	methods: {
		checkboxHeader (e) {
			if (e) {
				this.selectAll()
			} else {
				this.deselectAll()
			}
		},
		checkboxData (obj) {
			if (!this.selected.includes(obj)) {
				this.selected.push(obj)
			} else {
				for (var i = this.selected.length; i--;) {
					if (this.selected[i] === obj) this.selected.splice(i, 1);
				}
			}
		},
		filterUsername (val) {
			if (!this.filterUserNameValue) {
				return true;
			}

			return val.toLowerCase().includes(this.filterUserNameValue.toLowerCase());
		},
		clearFilters () {
			this.filterUserNameValue = '';
		},
		selectAll () {
			this.selected = this.respondents;
		},
		deselectAll () {
			this.selected = [];
		},
		updateSelected () {
			this.studyUuid = this.$store.getters.getSelectedStudy.studyUuid;
			this.loadData();
		},
		loadData () {
			const me = this;

			researcherApiService.loadRespondentAccounts(this.studyUuid)
				.then(function (response) {
					if (response && response.data) {
						me.respondents = response.data;
					}
				})
		},
		async checkTabMatch ({ tabIndex, subTabIndex } = {}) {
			var tabsPos = await utilityService.getTabsPositionByName('Manage Respondent Accounts', this.$studyTabConfig);
			if (tabsPos.tabPos === tabIndex && tabsPos.subtabPos === subTabIndex) {
				this.$refs.firstFocusable.focus();
				this.loadData();
			}
		},
		changeLockStatus (respondent) {
			const me = this;
			authService.updateLockStatus(respondent.userUuid, respondent.lockedOut)
				.then(function (response) {
					if (response && response.data) {
						me.$store.dispatch('changeUpdateStatusWithMessage', { status: 'generic-success', message: `User ${respondent.userName}'s lock status successfully updated` });
					} else {
						me.$store.dispatch('changeUpdateStatusWithMessage', { status: 'generic-error', message: `There was an error updating the user ${respondent.userName}'s lock status` });
					}
				}).catch(error => {
					me.$store.dispatch('changeUpdateStatusWithMessage', { status: 'generic-error', message: `There was an error updating the user ${respondent.userName}'s lock status` });
				});
		},
		resetPassword () {
			if (this.newPassword === this.confirmPassword) {
				this.errorConfirmPassword = false;
				if (this.$refs.form.validate()) {
					this.isPasswordResetLoading = true;
					const me = this;
					var body = {
						newPassword: this.newPassword
					}
					authService.changeRespondentPassword(body, this.resetPassRespondent.userUuid)
						.then(function (response) {
							if (response) {
								me.resetPasswordDialog = false;
								me.resetPasswordForm();
								console.log(response)
							}
							me.isPasswordResetLoading = false;
						})
						.catch(function (error) {
							me.isPasswordResetLoading = false;
							console.log(error)
						});
				}
			} else {
				this.errorConfirmPassword = true;
			}
		},
		resetPasswordForm () {
			this.newPassword = '';
			this.confirmPassword = '';
			this.resetPassRespondent = null;
		},
		deleteSelected () {
			const me = this;
			var accountInformation = {
				userUuids: []
			}

			for (var i = 0; i < this.selected.length; i++) {
				accountInformation.userUuids.push(this.selected[i].userUuid)
			}

			researcherApiService.deleteRespondentAccounts(this.studyUuid, accountInformation)
				.then(function (response) {
					if (response) {
						console.log(response);
						me.updateRespondents();
					}
				})
				.then(function (response) {
					me.$store.dispatch(
						'changeUpdateStatus',
						{ status: 'delete-respondents-success' }
					);
				})
				.catch(function (e) {
					let errorMessage = 'Deleting Respondents file failed.';
					if (e.response.data.messages) {
						errorMessage = e.response.data.messages[0];
					}
					me.$store.dispatch('changeUpdateStatusWithMessage', {
						status: 'delete-respondents-fail',
						message: errorMessage
					});
				})
		},
		updateRespondents () {
			const me = this;
			me.clearFilters();
			me.deselectAll();
			me.updateSelected();
		},
		getFormattedDate (date) {
			if (date && date.length) {
				return utilityService.formatDate(date)
			}
			return 'N/A';
		}
	}
}
</script>

<style>
</style>
